package one.devsbox.www.ui.cmd

import androidx.compose.animation.Crossfade
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.selection.SelectionContainer
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.key.*
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import compose.assets.Svg
import compose.assets.svg.ArrowLeft
import compose.assets.svg.CloseOutline
import compose.assets.svg.ResetOutline
import compose.assets.svg.TrashOutline
import kotlinx.browser.window
import kotlinx.datetime.Instant
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import one.devsbox.www.ui.widgets.FullscreenErrorUI
import one.devsbox.www.ui.widgets.FullscreenLoadingUI
import tk.mallumo.compose.navigation.ComposableNavNode
import tk.mallumo.utils.DataState

@Composable
@ComposableNavNode(one.devsbox.www.tools.ArgsId::class)
fun CmdUI() {
    with(CmdCtx.get()) {
        ContentCmdUI()
    }
}

@Composable
context(ctx: CmdCtx)
private fun ContentCmdUI() {
    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(Color(0xFF0C0C0D)) // Classic deep terminal black
    ) {
        Crossfade(targetState = ctx.vm.state) { state ->
            when (state) {
                is DataState.Idle -> Unit
                is DataState.Loading -> FullscreenLoadingUI()
                is DataState.Error -> {
                    FullscreenErrorUI(
                        message = state.message,
                        onBack = { ctx.action.back() },
                        onReload = { ctx.action.reconnect() }
                    )
                }

                is DataState.Result -> {
                    SuccessUI()
                }
            }
        }

        // Toast overlay
        ctx.vm.toastMessage?.let { msg ->
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(bottom = 96.dp),
                contentAlignment = Alignment.BottomCenter
            ) {
                Surface(
                    color = Color(0xFF1E1E24),
                    border = BorderStroke(1.dp, Color(0xFF00FFCC)),
                    shape = RoundedCornerShape(24.dp)
                ) {
                    Text(
                        text = msg,
                        color = Color.White,
                        fontFamily = FontFamily.Monospace,
                        style = MaterialTheme.typography.bodyMedium,
                        modifier = Modifier.padding(horizontal = 16.dp, vertical = 8.dp)
                    )
                }
            }
        }
    }
}

@Composable
context(ctx: CmdCtx)
private fun SuccessUI() {
    Column(
        modifier = Modifier
            .fillMaxSize()
            .navigationBarsPadding()
            .statusBarsPadding()
    ) {
        // Terminal Header
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 16.dp, vertical = 8.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            if (ctx.nav.canGoUp()) {
                IconButton(onClick = { ctx.action.back() }) {
                    Icon(
                        imageVector = Svg.ArrowLeft,
                        contentDescription = "Back",
                        tint = Color.White
                    )
                }
            } else {
                IconButton(onClick = { window.close() }) {
                    Icon(
                        imageVector = Svg.CloseOutline,
                        contentDescription = "Close",
                        tint = Color.White
                    )
                }
            }
            Spacer(modifier = Modifier.width(8.dp))
            Text(
                text = "DEVICE TERMINAL cmd_session #${ctx.args.id}",
                fontFamily = FontFamily.Monospace,
                fontWeight = FontWeight.Bold,
                color = Color(0xFF00FFCC), // Cyber Cyan
                style = MaterialTheme.typography.titleMedium
            )
            Spacer(modifier = Modifier.weight(1f))

            if (ctx.vm.logs.any { it.isPending }) {
                IconButton(onClick = { ctx.action.cancelPendingCommands() }) {
                    Box(
                        modifier = Modifier
                            .size(14.dp)
                            .background(Color(0xFFFF3333), shape = RoundedCornerShape(2.dp))
                    )
                }
            }

            // Reconnect / Reset WS
            IconButton(onClick = { ctx.action.reconnect() }) {
                Icon(
                    imageVector = Svg.ResetOutline,
                    contentDescription = "Reconnect",
                    tint = Color.White
                )
            }

            // Clear Log
            IconButton(onClick = { ctx.action.clearLogs() }) {
                Icon(
                    imageVector = Svg.TrashOutline,
                    contentDescription = "Clear Terminal Logs",
                    tint = Color.White
                )
            }
        }

        HorizontalDivider(color = Color(0xFF1E1E24))

        // Terminal Output logs
        val listState = rememberLazyListState()
        LaunchedEffect(ctx.vm.logs.size) {
            if (ctx.vm.logs.isNotEmpty()) {
                listState.animateScrollToItem(0)
            }
        }

        LazyColumn(
            state = listState,
            reverseLayout = true,
            modifier = Modifier
                .weight(1f)
                .fillMaxWidth()
                .padding(horizontal = 16.dp, vertical = 8.dp),
            verticalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            items(ctx.vm.logs) { log ->
                Card(
                    modifier = Modifier.fillMaxWidth(),
                    border = BorderStroke(1.dp, Color(0xFF1E1E24)),
                    colors = CardDefaults.cardColors(containerColor = Color(0xFF16161A)),
                    shape = RoundedCornerShape(4.dp)
                ) {
                    SelectionContainer {
                        Column(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(12.dp)
                        ) {
                            // Command prompt line
                            Row(
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Text(
                                    text = "$ ",
                                    fontFamily = FontFamily.Monospace,
                                    fontWeight = FontWeight.Bold,
                                    color = Color(0xFF4AF626) // Prompt Green
                                )
                                Text(
                                    text = log.command,
                                    fontFamily = FontFamily.Monospace,
                                    fontWeight = FontWeight.Bold,
                                    color = Color.White
                                )
                                if (log.isPending) {
                                    Spacer(modifier = Modifier.width(8.dp))
                                    CircularProgressIndicator(
                                        modifier = Modifier.size(12.dp),
                                        strokeWidth = 1.5.dp,
                                        color = Color(0xFF00FFCC)
                                    )
                                }
                            }

                            // Output lines
                            if (log.output.isNotEmpty()) {
                                Spacer(modifier = Modifier.height(8.dp))
                                log.output.forEach { line ->
                                    Text(
                                        text = line,
                                        fontFamily = FontFamily.Monospace,
                                        color = Color(0xFFE0E0E0),
                                        style = MaterialTheme.typography.bodySmall
                                    )
                                }
                            }

                            // Error lines
                            if (log.error.isNotEmpty()) {
                                Spacer(modifier = Modifier.height(8.dp))
                                log.error.forEach { line ->
                                    Text(
                                        text = line,
                                        fontFamily = FontFamily.Monospace,
                                        color = Color(0xFFFF5555),
                                        style = MaterialTheme.typography.bodySmall
                                    )
                                }
                            }

                            // Screenshot rendering
                            log.screenshot?.let { bitmap ->
                                Spacer(modifier = Modifier.height(8.dp))
                                Image(
                                    bitmap = bitmap,
                                    contentDescription = "Device Screenshot (Click to copy)",
                                    modifier = Modifier
                                        .width(500.dp)
                                        .aspectRatio(16f / 9f)
                                        .background(Color.Black)
                                        .clickable { ctx.action.copyScreenshotToClipboard(log) },
                                    contentScale = ContentScale.Fit
                                )
                            }

                            // Timestamps and duration info
                            Spacer(modifier = Modifier.height(8.dp))
                            val sentStr = formatTime(log.sentTime)
                            val recvStr = log.receivedTime?.let { formatTime(it) } ?: "Pending..."
                            val durationStr =
                                log.receivedTime?.let { formatDuration(it - log.sentTime) } ?: "..."
                            Text(
                                text = "Executed: $sentStr | Received: $recvStr | Duration: $durationStr",
                                fontFamily = FontFamily.Monospace,
                                color = Color.Gray,
                                style = MaterialTheme.typography.bodySmall
                            )
                        }
                    }
                }
            }
        }

        HorizontalDivider(color = Color(0xFF1E1E24))

        // Terminal Input panel
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(16.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            OutlinedTextField(
                value = ctx.vm.input,
                onValueChange = { ctx.vm.input = it },
                modifier = Modifier
                    .weight(1f)
                    .onPreviewKeyEvent { keyEvent ->
                        if (keyEvent.type == KeyEventType.KeyDown &&
                            keyEvent.key == Key.Enter &&
                            (keyEvent.isCtrlPressed || keyEvent.isAltPressed)
                        ) {
                            ctx.action.sendCommand()
                            true
                        } else {
                            false
                        }
                    },
                textStyle = LocalTextStyle.current.copy(
                    fontFamily = FontFamily.Monospace,
                    color = Color.White
                ),
                placeholder = {
                    Text(
                        text = "Enter terminal command...",
                        fontFamily = FontFamily.Monospace,
                        color = Color.Gray
                    )
                },
                singleLine = false,
                maxLines = 5,
                colors = OutlinedTextFieldDefaults.colors(
                    focusedBorderColor = Color(0xFF00FFCC),
                    unfocusedBorderColor = Color(0xFF1E1E24),
                    focusedContainerColor = Color(0xFF16161A),
                    unfocusedContainerColor = Color(0xFF16161A)
                )
            )

            Spacer(modifier = Modifier.width(8.dp))

            Button(
                onClick = { ctx.action.sendCommand() },
                colors = ButtonDefaults.buttonColors(
                    containerColor = Color(0xFF00FFCC),
                    contentColor = Color.Black
                ),
                shape = RoundedCornerShape(4.dp)
            ) {
                Text(
                    text = "EXEC",
                    fontFamily = FontFamily.Monospace,
                    fontWeight = FontWeight.Bold
                )
            }

            Spacer(modifier = Modifier.width(8.dp))

            Button(
                onClick = { ctx.action.takeScreenshot() },
                colors = ButtonDefaults.buttonColors(
                    containerColor = MaterialTheme.colorScheme.secondary,
                    contentColor = MaterialTheme.colorScheme.onSecondary
                ),
                shape = RoundedCornerShape(4.dp)
            ) {
                Text(
                    text = "SCREENSHOT",
                    fontFamily = FontFamily.Monospace,
                    fontWeight = FontWeight.Bold
                )
            }
        }
    }
}

private fun formatTime(epochMillis: Long): String {
    val instant = Instant.fromEpochMilliseconds(epochMillis)
    val localDateTime = instant.toLocalDateTime(TimeZone.currentSystemDefault())
    val year = localDateTime.year
    val month = localDateTime.month.toString().padStart(2, '0')
    val day = localDateTime.day.toString().padStart(2, '0')
    val hour = localDateTime.hour.toString().padStart(2, '0')
    val minute = localDateTime.minute.toString().padStart(2, '0')
    val second = localDateTime.second.toString().padStart(2, '0')
    val millis = (epochMillis % 1000).toString().padStart(3, '0')
    return "$year-$month-$day $hour:$minute:$second.$millis"
}

private fun formatDuration(millis: Long): String {
    return if (millis < 1000) {
        "${millis}ms"
    } else {
        val seconds = millis / 1000
        val hundredths = (millis % 1000) / 10
        "$seconds.${hundredths.toString().padStart(2, '0')}s"
    }
}

